import { Component, inject, OnInit, OnDestroy, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { NgIf } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { ContactService } from '../../services/contact.service';
import { CountUpDirective } from '../../shared/count-up.directive';

@Component({
  selector: 'app-home',
  imports: [NgIf, FormsModule, RouterLink, CountUpDirective],
  templateUrl: './home.html',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class Home implements OnInit, OnDestroy {
  submitted = false;
  name = '';
  email = '';
  message = '';

  private contact = inject(ContactService);
  private observer?: IntersectionObserver;
  private processObserver?: IntersectionObserver;

  ngOnInit() {
    this.initScrollAnimations();
  }

  ngOnDestroy() {
    if (this.observer) {
      this.observer.disconnect();
    }
    if (this.processObserver) {
      this.processObserver.disconnect();
    }
  }

  private initScrollAnimations() {
    // Intersection Observer für Scroll-Animationen
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate');
            // Nach erstem Trigger nicht weiter beobachten
            this.observer?.unobserve(entry.target);
          }
        });
      },
      {
        // Früheres Triggern: bereits ~200px vor Eintritt in den Viewport
        threshold: 0,
        rootMargin: '0px 0px 200px 0px'
      }
    );

    // Spezieller Observer für die Process Timeline (späterer Start)
    this.processObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate');
            this.processObserver?.unobserve(entry.target);
          }
        });
      },
      {
        // Immer noch leicht verzögert, aber früher als zuvor
        threshold: 0.15,
        rootMargin: '0px 0px 120px 0px'
      }
    );

    // Warte kurz, bis die Komponente vollständig geladen ist
    setTimeout(() => {
      // Beobachte die Service-Karten
      const serviceCards = document.querySelectorAll('.detailed-service-card');
      serviceCards.forEach((card) => {
        this.observer?.observe(card);
      });

      // Beobachte den Section-Header (Dienstleistungen)
      const sectionHeader = document.querySelector('.detailed-services-section .section-header');
      if (sectionHeader) {
        this.observer?.observe(sectionHeader);
      }

      // Beobachte Trust-Content Header
      const trustHeader = document.querySelector('.trust-content h2');
      if (trustHeader) {
        this.observer?.observe(trustHeader);
      }

      // Beobachte Trust-Items
      const trustItems = document.querySelectorAll('.trust-grid .trust-item');
      trustItems.forEach((item) => {
        this.observer?.observe(item);
      });

      // Beobachte About-Content Header
      const aboutHeader = document.querySelector('.about-content .section-header');
      if (aboutHeader) {
        this.observer?.observe(aboutHeader);
      }

      // Beobachte About-Highlights
      const aboutHighlights = document.querySelectorAll('.about-content .highlight-text');
      aboutHighlights.forEach((el) => this.observer?.observe(el));

      // Beobachte Expertise-Items
      const expertiseItems = document.querySelectorAll('.about-content .expertise-item');
      expertiseItems.forEach((el) => this.observer?.observe(el));

      // Beobachte Mission-Box
      const missionBox = document.querySelector('.about-content .mission-box');
      if (missionBox) {
        this.observer?.observe(missionBox);
      }

      // Beobachte Company-Info Card
      const companyCard = document.querySelector('.about-content .company-info-card');
      if (companyCard) {
        this.observer?.observe(companyCard);
      }

      // Beobachte Secondary-Stats
      const secondaryStats = document.querySelectorAll('.about-content .secondary-stat');
      secondaryStats.forEach((el) => this.observer?.observe(el));

      // Beobachte Contact-Form Sektion
      const contactHeader = document.querySelector('.contact-form-section .contact-form-header');
      if (contactHeader) {
        this.observer?.observe(contactHeader);
      }
      const contactInfo = document.querySelector('.contact-form-section .contact-info-box');
      if (contactInfo) {
        this.observer?.observe(contactInfo);
      }

      // Beobachte Process Timeline Schritte mit strengerer Schwelle
      const processSteps = document.querySelectorAll('.process-timeline .process-step');
      processSteps.forEach((step) => {
        this.processObserver?.observe(step);
      });
    }, 100);
  }

  scrollToContact(event: Event) {
    event.preventDefault();
    const contactSection = document.getElementById('kontakt');
    if (contactSection) {
      contactSection.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  }

  onSubmit(e: Event) {
    e.preventDefault();
    const payload = { name: this.name, email: this.email, message: this.message };
    // Sende Daten; bei Fehler weiterhin Erfolgsmeldung zeigen, um UX konsistent zu halten
    this.contact.submitContact(payload).subscribe({
      next: () => (this.submitted = true),
      error: () => (this.submitted = true),
    });
  }
}
